/**
 * @description Displays a report of all the personnel affected by the syncs by row number
 */

import React from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Link from '@mui/material/Link';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import NoResults from '@seccon/shared/ui/components/NoResults';

import { isEmpty } from 'lodash';
import { SyncResultsContext } from '../contexts/SyncResults';

const styles = {
  heading: {
    width: '100%'
  },
  innerTableContainer: {
    width: '50%',
    minWidth: '900px'
  },
  outerTableContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    width: '100%'
  },
  rowNumeric: {
    textAlign: 'center'
  },
  subheading: {
    color: 'text.secondary'
  },
  table: {
    width: '100%'
  },
  tableColumnRowNumber: {
    textAlign: 'center',
    textWrap: 'nowrap',
    width: '100px'
  }
}

const SuccessfulSyncs = () => {
  const results = React.useContext(SyncResultsContext);
  const { personnelSyncedRecordList = [], fromHistory } = results ?? {};

  // we dont save the personnel in sync historys so do not show this or empty
  // or if this is empty, the successfulSyncs will also be 0 so dont need double indicator
  if (fromHistory || isEmpty(personnelSyncedRecordList)) return null

  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [page, setPage] = React.useState(0);

  function handlePageChange (newValue) {
    setPage(newValue);
  }

  function handleChangeRowsPerPage (newValue) {
    setRowsPerPage(newValue);
    setPage(0);
  }

  const [pageData, total] = React.useMemo(() => [
    personnelSyncedRecordList.slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage),
    personnelSyncedRecordList.length
  ], [personnelSyncedRecordList, page, rowsPerPage]);

  return (
    <>
      <Box>
        <Typography sx={styles.heading} variant='h6'>Personnel Affected By Sync</Typography>
      </Box>
      {total > 0 ? (
        <Box sx={styles.outerTableContainer}>
          <Paper sx={styles.innerTableContainer}>
            <TableContainer>
              <Table sx={styles.table}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={styles.tableColumnRowNumber}>Row Number</TableCell>
                    <TableCell>Personnel</TableCell>
                    <TableCell>Facility CAGE Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageData.map((personnel) => (
                    <TableRow key={`row${ personnel.id }`} sx={styles.tableBodyRow}>
                      <TableCell>{personnel.rowNum}</TableCell>
                      <TableCell>
                        <Link
                          href={`/personnel/show/${ personnel.id }`}
                          target='_blank'
                        >
                          {personnel.fullName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {personnel.facilityCageCode}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component='div'
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, value) => handlePageChange(value)}
              onRowsPerPageChange={(evt) => handleChangeRowsPerPage(parseInt(evt.target.value, 10))}
            />
          </Paper>
        </Box>
      ) : (
        <NoResults Icon={CheckCircleOutlineOutlinedIcon}>No Personnel Affected By Sync</NoResults>
      )}
    </>
  );
};

export default SuccessfulSyncs;
